import { useCallback, useEffect, useState } from 'react';
import { useDropzone } from 'react-dropzone';
import { X, Upload, Loader2, Check, FileText, Search } from 'lucide-react';
import { api } from '../../lib/api';
import type { MediaItem } from '../../lib/types';

type MediaFolder = 'properties' | 'agents' | 'posts' | 'settings' | 'pages' | 'general';

function toWebP(file: File, folder: string, quality = 0.85): Promise<File> {
  if (!file.type.startsWith('image/') || file.type === 'image/svg+xml' || folder === 'settings') return Promise.resolve(file);
  return new Promise((resolve, reject) => {
    const img = new Image();
    const url = URL.createObjectURL(file);
    img.onload = () => {
      URL.revokeObjectURL(url);
      const canvas = document.createElement('canvas');
      canvas.width = img.naturalWidth;
      canvas.height = img.naturalHeight;
      canvas.getContext('2d')!.drawImage(img, 0, 0);
      canvas.toBlob(
        (blob) => {
          if (!blob) return reject(new Error('Canvas toBlob failed'));
          resolve(new File([blob], file.name.replace(/\.[^.]+$/, '.webp'), { type: 'image/webp' }));
        },
        'image/webp',
        quality,
      );
    };
    img.onerror = () => { URL.revokeObjectURL(url); reject(new Error('Image load failed')); };
    img.src = url;
  });
}

function formatBytes(bytes: number | null): string {
  if (!bytes) return '';
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(0)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

interface Props {
  open: boolean;
  onClose: () => void;
  onSelect: (urls: string[]) => void;
  multiple?: boolean;
  folder?: MediaFolder;
  accept?: 'images' | 'pdf';
  currentSelected?: string[];
}

export function MediaLibraryModal({ open, onClose, onSelect, multiple = true, folder = 'general', accept = 'images', currentSelected = [] }: Props) {
  const [items, setItems] = useState<MediaItem[]>([]);
  const [loading, setLoading] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [selected, setSelected] = useState<Set<string>>(new Set(currentSelected));
  const [search, setSearch] = useState('');
  const [folderFilter, setFolderFilter] = useState<string>('all');
  const [uploadError, setUploadError] = useState<string | null>(null);

  useEffect(() => {
    if (!open) return;
    setSelected(new Set(currentSelected));
    setSearch('');
    setFolderFilter('all');
    setUploadError(null);
    setLoading(true);
    api.media.list().then(setItems).catch(console.error).finally(() => setLoading(false));
  }, [open]);

  const dropzoneAccept: Record<string, string[]> = accept === 'pdf'
    ? { 'application/pdf': [] }
    : { 'image/*': [] };

  const onDrop = useCallback(async (acceptedFiles: File[]) => {
    setUploading(true);
    setUploadError(null);
    try {
      const uploaded = await Promise.all(
        acceptedFiles.map(file => toWebP(file, folder).then(f => api.media.upload(f, folder)))
      );
      const newItems = uploaded as unknown as MediaItem[];
      setItems(prev => [...newItems, ...prev]);
      const newUrls = newItems.map(r => r.url);
      if (multiple) {
        setSelected(prev => new Set([...prev, ...newUrls]));
      } else {
        setSelected(new Set([newUrls[0]]));
      }
    } catch {
      setUploadError('Error al subir. Intenta de nuevo.');
    } finally {
      setUploading(false);
    }
  }, [folder, multiple]);

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    accept: dropzoneAccept,
    multiple,
  });

  const toggle = (url: string) => {
    if (multiple) {
      setSelected(prev => {
        const next = new Set(prev);
        next.has(url) ? next.delete(url) : next.add(url);
        return next;
      });
    } else {
      setSelected(new Set([url]));
    }
  };

  const confirm = () => {
    onSelect([...selected]);
    onClose();
  };

  const isPdf = (item: MediaItem) => item.mimeType === 'application/pdf';

  const FOLDER_LABELS: Record<string, string> = {
    pages: 'Páginas',
    properties: 'Propiedades',
    agents: 'Agentes',
    posts: 'Blog',
    settings: 'Ajustes',
    general: 'General',
  };

  // Derive which folders actually have items (for this accept type)
  const availableFolders = [...new Set(
    items
      .filter(item => accept === 'pdf' ? item.mimeType === 'application/pdf' : item.mimeType !== 'application/pdf')
      .map(item => item.r2Key.split('/')[1] ?? 'general')
  )].sort();

  const filtered = items.filter(item => {
    if (accept === 'pdf' && item.mimeType !== 'application/pdf') return false;
    if (accept === 'images' && item.mimeType === 'application/pdf') return false;
    if (folderFilter !== 'all' && item.r2Key.split('/')[1] !== folderFilter) return false;
    if (search) return item.filename.toLowerCase().includes(search.toLowerCase());
    return true;
  });

  if (!open) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      {/* Backdrop */}
      <div className="absolute inset-0 bg-black/70" onClick={onClose} />

      {/* Modal */}
      <div className="relative bg-ui-surface border border-ui-border rounded-xl shadow-2xl w-full max-w-4xl max-h-[85vh] flex flex-col">

        {/* Header */}
        <div className="flex items-center justify-between px-5 py-4 border-b border-ui-border flex-shrink-0">
          <h2 className="text-base font-semibold text-ui-text">Biblioteca de archivos e imágenes</h2>
          <button onClick={onClose} className="text-ui-text-muted hover:text-ui-text transition-colors">
            <X size={20} />
          </button>
        </div>

        {/* Upload zone */}
        <div className="px-5 pt-4 pb-3 flex-shrink-0">
          <div
            {...getRootProps()}
            className={`
              flex items-center gap-3 border-2 border-dashed rounded-lg px-4 py-3 cursor-pointer transition-colors
              ${isDragActive ? 'border-ui-accent bg-ui-accent/10' : 'border-ui-border hover:border-ui-border-strong'}
              ${uploading ? 'pointer-events-none opacity-50' : ''}
            `}
          >
            <input {...getInputProps()} />
            {uploading
              ? <Loader2 size={18} className="animate-spin text-ui-accent flex-shrink-0" />
              : accept === 'pdf'
                ? <FileText size={18} className="text-ui-text-muted flex-shrink-0" />
                : <Upload size={18} className="text-ui-text-muted flex-shrink-0" />
            }
            <div>
              <p className="text-sm text-ui-text">
                {uploading ? 'Subiendo...' : isDragActive ? 'Suelta aquí' : accept === 'pdf' ? 'Subir nuevo PDF' : 'Subir nueva imagen'}
              </p>
              <p className="text-sm text-ui-text-subtle">
                {accept === 'pdf' ? 'PDF' : 'JPG, PNG, WebP, SVG — se convierte a WebP automáticamente'}
              </p>
            </div>
          </div>
          {uploadError && <p className="mt-1.5 text-sm text-ui-danger">{uploadError}</p>}
        </div>

        {/* Folder filter pills */}
        {availableFolders.length > 1 && (
          <div className="px-5 pb-3 flex items-center gap-2 flex-wrap flex-shrink-0">
            <button
              type="button"
              onClick={() => setFolderFilter('all')}
              className={`px-3 py-1 rounded-full text-xs font-medium transition-colors ${
                folderFilter === 'all'
                  ? 'bg-ui-accent text-ui-text-inverse'
                  : 'bg-ui-surface text-ui-text-muted hover:text-ui-text hover:bg-ui-surface-2'
              }`}
            >
              Todos
            </button>
            {availableFolders.map(f => (
              <button
                key={f}
                type="button"
                onClick={() => setFolderFilter(f)}
                className={`px-3 py-1 rounded-full text-xs font-medium transition-colors ${
                  folderFilter === f
                    ? 'bg-ui-accent text-ui-text-inverse'
                    : 'bg-ui-surface text-ui-text-muted hover:text-ui-text hover:bg-ui-surface-2'
                }`}
              >
                {FOLDER_LABELS[f] ?? f}
              </button>
            ))}
          </div>
        )}

        {/* Search + count */}
        <div className="px-5 pb-3 flex items-center gap-3 flex-shrink-0">
          <div className="relative flex-1">
            <Search size={14} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-ui-text-subtle" />
            <input
              type="text"
              placeholder="Buscar por nombre..."
              value={search}
              onChange={e => setSearch(e.target.value)}
              className="w-full bg-ui-bg border border-ui-border rounded-md pl-8 pr-3 py-1.5 text-sm text-ui-text placeholder-ui-text-subtle focus:outline-none focus:border-ui-accent"
            />
          </div>
          {selected.size > 0 && (
            <span className="text-xs text-ui-accent font-medium whitespace-nowrap">
              {selected.size} seleccionada{selected.size > 1 ? 's' : ''}
            </span>
          )}
        </div>

        {/* Grid */}
        <div className="flex-1 overflow-y-auto px-5 pb-3 min-h-0">
          {loading ? (
            <div className="flex items-center justify-center h-32">
              <Loader2 size={24} className="animate-spin text-ui-text-subtle" />
            </div>
          ) : filtered.length === 0 ? (
            <div className="flex flex-col items-center justify-center h-32 text-ui-text-subtle">
              <p className="text-sm">{search ? 'Sin resultados' : 'Aún no hay archivos subidos'}</p>
              {!search && <p className="text-xs mt-1">Sube una imagen usando la zona de arriba</p>}
            </div>
          ) : (
            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-3">
              {filtered.map(item => {
                const isSelected = selected.has(item.url);
                return (
                  <button
                    key={item.id}
                    type="button"
                    onClick={() => toggle(item.url)}
                    className={`
                      relative group aspect-square rounded-lg overflow-hidden border-2 transition-all text-left
                      ${isSelected ? 'border-ui-accent ring-1 ring-ui-accent' : 'border-ui-border hover:border-ui-border-strong'}
                    `}
                    title={item.filename}
                  >
                    {isPdf(item) ? (
                      <div className="w-full h-full bg-ui-surface flex flex-col items-center justify-center gap-1 p-2">
                        <FileText size={24} className="text-ui-text-muted" />
                        <span className="text-xs text-ui-text-subtle text-center leading-tight break-all line-clamp-2">{item.filename}</span>
                      </div>
                    ) : (
                      <img src={item.url} alt={item.filename} className="w-full h-full object-cover" />
                    )}

                    {/* Selected checkmark */}
                    {isSelected && (
                      <div className="absolute top-1 right-1 w-5 h-5 bg-ui-accent rounded-full flex items-center justify-center">
                        <Check size={12} className="text-ui-text-inverse" />
                      </div>
                    )}

                    {/* Hover overlay with filename */}
                    <div className="absolute inset-0 bg-black/60 opacity-0 group-hover:opacity-100 transition-opacity flex items-end p-1.5 pointer-events-none">
                      <span className="text-xs text-ui-text-inverse leading-tight line-clamp-2 break-all">{item.filename}</span>
                      {item.sizeBytes && <span className="text-xs text-ui-text-inverse/70 ml-auto flex-shrink-0">{formatBytes(item.sizeBytes)}</span>}
                    </div>
                  </button>
                );
              })}
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="flex items-center justify-between px-5 py-4 border-t border-ui-border flex-shrink-0">
          <button
            type="button"
            onClick={onClose}
            className="text-sm text-ui-text-muted hover:text-ui-text transition-colors"
          >
            Cancelar
          </button>
          <button
            type="button"
            onClick={confirm}
            disabled={selected.size === 0}
            className="px-4 py-2 bg-ui-accent text-ui-text-inverse text-sm font-medium rounded-lg disabled:opacity-40 disabled:cursor-not-allowed hover:opacity-90 transition-opacity"
          >
            {selected.size === 0
              ? 'Seleccionar'
              : `Usar ${selected.size === 1 ? 'imagen' : `${selected.size} imágenes`}`}
          </button>
        </div>
      </div>
    </div>
  );
}
